import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import './PromoModal.css';

const PromoModal = () => {
  const [modalConfig, setModalConfig] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Listen to Firestore for real-time updates
    const unsubscribe = onSnapshot(
      doc(db, 'site_config', 'promotional_modal'),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();

          // Check if modal should be shown based on dates and active status
          const shouldShow = checkIfShouldShow(data);

          if (shouldShow) {
            setModalConfig(data);

            // Check session storage to see if user already dismissed this session
            const dismissedThisSession = sessionStorage.getItem('promo_modal_dismissed');

            if (!dismissedThisSession) {
              // Small delay for smooth entrance
              setTimeout(() => setIsVisible(true), 500);
            }
          } else {
            setIsVisible(false);
            setModalConfig(null);
          }
        }
      },
      (error) => {
        console.error('Error fetching promotional modal config:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  const checkIfShouldShow = (config) => {
    if (!config || !config.active) return false;

    // Check date range if specified
    if (config.startDate || config.endDate) {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Start of today

      if (config.startDate) {
        const startDate = new Date(config.startDate);
        startDate.setHours(0, 0, 0, 0);
        if (now < startDate) return false;
      }

      if (config.endDate) {
        const endDate = new Date(config.endDate);
        endDate.setHours(23, 59, 59, 999); // End of the day
        if (now > endDate) return false;
      }
    }

    return true;
  };

  const handleClose = () => {
    setIsClosing(true);

    // Mark as dismissed for this session
    sessionStorage.setItem('promo_modal_dismissed', 'true');

    // Wait for animation to complete
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleButtonClick = () => {
    handleClose();
    // Let the link navigate naturally
  };

  if (!isVisible || !modalConfig) return null;

  // Determine which template to render
  const renderModalContent = () => {
    switch (modalConfig.designType) {
      case 'festive':
        return <FestiveTemplate config={modalConfig} onClose={handleClose} onButtonClick={handleButtonClick} />;
      case 'flashSale':
        return <FlashSaleTemplate config={modalConfig} onClose={handleClose} onButtonClick={handleButtonClick} />;
      case 'classic':
      default:
        return <ClassicTemplate config={modalConfig} onClose={handleClose} onButtonClick={handleButtonClick} />;
    }
  };

  return (
    <div className={`promo-modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="promo-modal-content" onClick={(e) => e.stopPropagation()}>
        {renderModalContent()}
      </div>
    </div>
  );
};

// Classic Template
const ClassicTemplate = ({ config, onClose, onButtonClick }) => (
  <div className="promo-modal classic-template">
    <button className="promo-modal-close" onClick={onClose} aria-label="Close">
      ✕
    </button>

    {config.imageUrl && (
      <div className="promo-modal-image">
        <img src={config.imageUrl} alt={config.title} />
      </div>
    )}

    <div className="promo-modal-body">
      <h2 className="promo-modal-title">{config.title}</h2>
      <p className="promo-modal-message">{config.message}</p>

      {config.buttonText && config.buttonLink && (
        <a
          href={config.buttonLink}
          className="promo-modal-button"
          onClick={onButtonClick}
        >
          {config.buttonText}
        </a>
      )}
    </div>
  </div>
);

// Festive Template
const FestiveTemplate = ({ config, onClose, onButtonClick }) => (
  <div className="promo-modal festive-template">
    <button className="promo-modal-close" onClick={onClose} aria-label="Close">
      ✕
    </button>

    <div className="festive-decoration">
      <span className="festive-star">⭐</span>
      <span className="festive-star">✨</span>
      <span className="festive-star">🎉</span>
    </div>

    {config.imageUrl && (
      <div className="promo-modal-image festive-image">
        <img src={config.imageUrl} alt={config.title} />
      </div>
    )}

    <div className="promo-modal-body">
      <h2 className="promo-modal-title festive-title">{config.title}</h2>
      <p className="promo-modal-message festive-message">{config.message}</p>

      {config.buttonText && config.buttonLink && (
        <a
          href={config.buttonLink}
          className="promo-modal-button festive-button"
          onClick={onButtonClick}
        >
          {config.buttonText}
        </a>
      )}
    </div>

    <div className="festive-decoration bottom">
      <span className="festive-star">🎊</span>
      <span className="festive-star">✨</span>
      <span className="festive-star">🎈</span>
    </div>
  </div>
);

// Flash Sale Template
const FlashSaleTemplate = ({ config, onClose, onButtonClick }) => (
  <div className="promo-modal flash-sale-template">
    <button className="promo-modal-close flash-close" onClick={onClose} aria-label="Close">
      ✕
    </button>

    <div className="flash-badge">
      <span className="flash-icon">⚡</span>
      <span className="flash-text">FLASH SALE</span>
      <span className="flash-icon">⚡</span>
    </div>

    {config.imageUrl && (
      <div className="promo-modal-image flash-image">
        <img src={config.imageUrl} alt={config.title} />
      </div>
    )}

    <div className="promo-modal-body flash-body">
      <h2 className="promo-modal-title flash-title">{config.title}</h2>
      <p className="promo-modal-message flash-message">{config.message}</p>

      {config.buttonText && config.buttonLink && (
        <a
          href={config.buttonLink}
          className="promo-modal-button flash-button"
          onClick={onButtonClick}
        >
          <span className="button-glow">{config.buttonText}</span>
        </a>
      )}
    </div>

    <div className="flash-urgency">
      Limited Time Only!
    </div>
  </div>
);

export default PromoModal;
